/* eslint-disable jsx-a11y/no-static-element-interactions */
import { AutocompleteWithLoading, TimeSelector, ConciseSelector } from "@iventis/components";
import { RelativeEventRuleRelationship } from "@iventis/domain-model/model/relativeEventRuleRelationship";
import { TimeRelativeTo } from "@iventis/domain-model/model/timeRelativeTo";
import { TimeSpan, Preposition } from "@iventis/types";
import { formatTimeSpan, ZERO_TIME_SPAN, formatDateInTimezone, EMPTY_GUID } from "@iventis/utilities";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { customMediaQueryMax, screenSizeBreakpoints, muiInputFormsCSS, customMediaQueryMin, media, Label1, styled } from "@iventis/styles";
import { Content } from "@iventis/translations/content/typed-content";
import { RelativeEventRule } from "@iventis/domain-model/model/relativeEventRule";
import { RecurringEventRule } from "@iventis/domain-model/model/recurringEventRule";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { format } from "date-fns";
import { RecurrenceType } from "@iventis/domain-model/model/recurrenceType";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { Theme } from "@emotion/react";
import { UseIventisTranslate, useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { checkboxWithLabelCss } from "@iventis/styles/src/atomic-rules";
import { CheckBoxStyled } from "@iventis/components/src/checkbox";
import { EventBlockTimeProperties, SetFieldValue } from "./schedule.types";
import { TimeSelectorWrapper } from "./formik-components/wrappers/time-selector-wrapper";
import { isTimeStringValid, SortEventBlocksByStartAt } from "./schedule-helper-functions";
import { getDefaultRelativeToValue, notAllowedTimeRelativeToByCalendarLevel } from "./time-rules.helpers";

export type TimeRuleFormValues = {
    time: string;
    preposition: Preposition;
    relativeTo: TimeRelativeTo;
    relationship: RelativeEventRuleRelationship;
    specificRelatedToEvent: { id: string; name: string; type: ScheduleConfigEventBlockType; startAt?: Date; endAt?: Date };
    relatedType: ScheduleConfigEventBlockType;
    doesRepeatActivity: boolean;
    repeatActivity: string[];
    duration: TimeSpan;
};

const translatePreposition = (translate: UseIventisTranslate): { [key in Preposition]: string } => ({
    [Preposition.Before]: translate(Content.timerules.preposition.before),
    [Preposition.After]: translate(Content.timerules.preposition.after),
});

interface PotentialEventBlocksToRelateTo {
    id: string;
    name: string;
    type: ScheduleConfigEventBlockType;
    startAt?: Date;
    endAt?: Date;
}

/**
 * A component which has inputs for a form for relative time rules
 * @param setFieldValue - a callback function which is used to pass values up to the component above
 * @param values - the current values for the inputs
 * @param potentialEventBlocksToRelateTo - list of event blocks which the event item can be related to
 * @param eventBlockTypes - list of event block types being used
 * @param scheduleDaysMap - day number of the event with the actual date
 * @param currentDate - day which is selected
 * @param defaultRelatedToSpecificEventBlockId - e.g. if related to specific session and we're a scheduled item, this should be the parent session id
 * @param disableInputs - a key value pair, key is the name of the input to be disabled, value is a boolean if true then the input is disabled
 * @param hiddenValues - a key value pair, key is the time form property and value is an array of values (options) to hide from the user
 * @param hiddenValuesBasedOnRelatedType - a key value pair, key is the type of event block where the filtering of values take affect, value is a key value pair
 *  where the key is name of the input where the list of values can be filtered and the value is the list items that are hidden
 * @param hiddenInputs - a key value pair, key is the name of the input to be hidden, value is a boolean if true then the input is not shown in the form
 * @param initialFormValues - initial values for the form for every time it is rendered
 */
export const TimeRulesEditor: FunctionComponent<{
    setFieldValue: SetFieldValue<EventBlockTimeProperties>;
    values: EventBlockTimeProperties;
    potentialEventBlocksToRelateTo: PotentialEventBlocksToRelateTo[];
    eventBlockTypes: ScheduleConfigEventBlockType[];
    currentEventBlockType: ScheduleConfigEventBlockType;
    scheduleDaysMap: Map<number, Date>;
    currentDate: Date;
    disableInputs: Partial<Record<keyof TimeRuleFormValues, (values: TimeRuleFormValues) => boolean>>;
    hiddenInputs?: Partial<Record<keyof TimeRuleFormValues, (values: TimeRuleFormValues) => boolean>>;
    hiddenValuesBasedOnRelatedType?: Partial<Record<number, { relativeTo: TimeRelativeTo[] }>>;
    initialFormValues?: Partial<TimeRuleFormValues>;
    timezone: string;
}> = ({
    values,
    eventBlockTypes,
    currentEventBlockType,
    scheduleDaysMap,
    currentDate,
    setFieldValue,
    potentialEventBlocksToRelateTo,
    disableInputs,
    hiddenInputs,
    hiddenValuesBasedOnRelatedType,
    initialFormValues,
    timezone,
}) => {
    const translate = useIventisTranslate();
    const { relativeEventRules } = values;

    // Event block types will never change - hence no dependencies
    // Sorted by calendar level
    const relatableEventBlockTypes = useMemo(
        () => currentEventBlockType.allowedRelativeTypes?.map((id) => eventBlockTypes.find((t) => t.id === id)).sort((a, b) => (a.calendarLevel > b.calendarLevel ? 1 : -1)),
        []
    );

    /*
        Initial values
    */
    const initialRelativeEventRule = useMemo(() => relativeEventRules?.length > 0 && relativeEventRules[0], []);
    const initialTime = useMemo(
        // The time form value must be the absolute value off the offset, so we remove the "-" if it's a negative offset
        () => (initialRelativeEventRule?.offset?.startsWith("-") ? (initialRelativeEventRule?.offset as string)?.slice(1) : initialRelativeEventRule?.offset),
        []
    );
    const initialRelatedEventBlockType = useMemo(
        () =>
            (initialRelativeEventRule && eventBlockTypes.find(({ id }) => id === initialRelativeEventRule.relativeToEventBlockTypeId)) ||
            eventBlockTypes.find(({ id }) => relatableEventBlockTypes[0].id === id), // Always select the highest level relatable event block type by default
        []
    );
    const initialPreposition = initialRelativeEventRule ? ((initialRelativeEventRule.offset as string)[0] === "-" ? Preposition.Before : Preposition.After) : Preposition.Before;
    const allDays = Array.from(scheduleDaysMap.keys()).map((day) => day.toString());
    const initialDoesRepeat = useMemo(
        // Currently only support all or none, so we check against the EVERY recurrence type to determine intial state
        () => !!(initialRelativeEventRule?.recurringEventRules && initialRelativeEventRule?.recurringEventRules[0]?.recurrenceType === RecurrenceType.Every),
        []
    );

    /*
        Form values
    */
    const [state, setState] = useState<TimeRuleFormValues>({
        time: initialFormValues?.time == null ? initialTime || ZERO_TIME_SPAN : initialFormValues?.time,
        preposition: initialFormValues?.preposition == null ? initialPreposition : initialFormValues?.preposition,
        relativeTo: initialFormValues?.relativeTo == null ? initialRelativeEventRule?.relativeTo || TimeRelativeTo.StartTime : initialFormValues?.relativeTo,
        relationship: initialFormValues?.relationship == null ? initialRelativeEventRule?.relationship || RelativeEventRuleRelationship.Every : initialFormValues.relationship,
        specificRelatedToEvent:
            initialFormValues?.specificRelatedToEvent == null
                ? potentialEventBlocksToRelateTo.find((eb) => eb.id === initialRelativeEventRule?.relativeToSpecificEventBlock?.id) || null
                : initialFormValues?.specificRelatedToEvent,
        relatedType: initialFormValues?.relatedType == null ? initialRelatedEventBlockType : initialFormValues?.relatedType,
        doesRepeatActivity: initialFormValues?.doesRepeatActivity == null ? initialDoesRepeat : initialFormValues?.doesRepeatActivity,
        repeatActivity: initialFormValues?.repeatActivity == null ? allDays : initialFormValues?.repeatActivity,
        duration: initialFormValues?.duration == null ? initialRelativeEventRule.duration || ZERO_TIME_SPAN : initialFormValues?.duration,
    });
    const { time, preposition, relativeTo, relationship, specificRelatedToEvent, relatedType, doesRepeatActivity, repeatActivity, duration } = state;

    const setFormValue: SetFieldValue<TimeRuleFormValues> = (key, value) => setState({ ...state, [key]: value });

    const setFormValues = (newState: Partial<TimeRuleFormValues>) => setState({ ...state, ...newState });

    /*
        Derived domain values
    */
    const offset = useMemo(() => {
        const [hours, minutes, seconds] = time?.split(":");
        return formatTimeSpan(preposition, hours, minutes, seconds);
    }, [preposition, time]);

    const timeRelativeToOptions: TimeRelativeTo[] = useMemo(() => {
        const timeRelativeToOptions = Object.values(TimeRelativeTo);
        // if hidden values array contains relativeTo property and is not null
        if (hiddenValuesBasedOnRelatedType?.[relatedType?.calendarLevel]?.relativeTo != null) {
            // filter values from timeRelativeToOptions with the relativeTo property array
            const hiddenRelativeToOptions = hiddenValuesBasedOnRelatedType[relatedType.calendarLevel].relativeTo;
            return timeRelativeToOptions.filter((o) => !hiddenRelativeToOptions.includes(o));
        }
        return timeRelativeToOptions;
    }, [relatedType]);

    const newRelativeEventRule = useMemo<RelativeEventRule>(() => {
        // Currently we only support no repeat and repeat on all days
        const recurringEventRule = doesRepeatActivity
            ? ({
                  ...values.relativeEventRules?.[0]?.recurringEventRules?.[0],
                  recurrenceType: RecurrenceType.Every,
                  startOfRecurrence: scheduleDaysMap.get(0),
                  endOfRecurrence: Array.from(scheduleDaysMap.values()).pop(),
                  recurringDates: [],
              } as RecurringEventRule)
            : ({
                  ...values.relativeEventRules?.[0]?.recurringEventRules?.[0],
                  recurrenceType: RecurrenceType.SpecificDates,
                  recurringDates: [currentDate],
                  startOfRecurrence: null,
                  endOfRecurrence: null,
                  recurringDaysInCycle: [],
              } as RecurringEventRule);

        return {
            id: relativeEventRules?.length > 0 ? relativeEventRules[0].id : EMPTY_GUID,
            offset,
            relativeTo,
            relationship,
            relativeToEventBlockTypeId: relatedType.id,
            relativeToSpecificEventBlock: relationship === RelativeEventRuleRelationship.Specific ? specificRelatedToEvent : null,
            recurringEventRules: relationship === RelativeEventRuleRelationship.Specific ? [] : [recurringEventRule],
            duration,
        } as RelativeEventRule;
    }, [offset, relativeTo, relationship, relatedType, doesRepeatActivity, repeatActivity, specificRelatedToEvent, duration]);

    /*
        Other
    */
    const timeErrorCheck = (value: string) => {
        if (value === "" || !isTimeStringValid(value)) {
            return translate(Content.schedule2.validation.startTimeValid);
        }
        return null;
    };

    const eventBlocksToRelateToOptions = useMemo(
        () =>
            potentialEventBlocksToRelateTo
                .filter((eventBlock) => eventBlock.type.id === state.relatedType.id)
                .sort((a, b) => SortEventBlocksByStartAt(a as EventBlock, b as EventBlock)),
        [state.relatedType.id]
    );

    /*
        Callbacks
    */
    useEffect(() => {
        setFieldValue("relativeEventRules", [newRelativeEventRule]);
    }, [newRelativeEventRule]);

    useEffect(() => {
        // if relatedType is not the same type as the current event block, we must adjust certain values
        if (state.specificRelatedToEvent?.type?.id !== state.relatedType.id) {
            // Get the relevant event block to relate to (or just the last)
            const specificRelatedToEvent =
                eventBlocksToRelateToOptions.find(
                    (eventBlock) => currentEventBlockType?.allowedRelativeTypes?.[0] === state.relatedType.id && currentEventBlockType?.allowedRelativeTypes?.[0] === eventBlock.id
                ) || eventBlocksToRelateToOptions?.[eventBlocksToRelateToOptions?.length - 1];
            // set values, specificRelatedToEvent to the first valid event block, relativeTo to the default value for the event block type
            if (notAllowedTimeRelativeToByCalendarLevel(state.relatedType?.calendarLevel).includes(state.relativeTo)) {
                setFormValues({ specificRelatedToEvent, relativeTo: getDefaultRelativeToValue(state.relatedType) });
            } else {
                setFormValues({ specificRelatedToEvent });
            }
        }
    }, [state.relatedType.id, eventBlocksToRelateToOptions]);

    return (
        <StyledFormContainer>
            <div className="field-group top-time-type-row">
                <StyledFieldLabel>{translate(Content.timerules.start_activity)}</StyledFieldLabel>

                <div className="line-items-large-display">
                    <TimeSelector
                        disabled={!!disableInputs.time?.(state)}
                        className="time-selector"
                        onChange={(e) => setFormValue("time", e.target.value)}
                        value={time}
                        error={!!timeErrorCheck(time)}
                        helperText={timeErrorCheck(time)}
                    />
                    <AutocompleteWithLoading
                        value={preposition}
                        id="after-before-selector"
                        options={Object.values(Preposition)}
                        onChange={(value) => setFormValue("preposition", value as Preposition)}
                        getOptionLabel={(option: Preposition) => translatePreposition(translate)[option]}
                        getOptionSelected={(option) => option === preposition}
                        loadingOptions={false}
                        disableClearable
                        disabled={!!disableInputs.preposition?.(state)}
                        style={{ flexGrow: 1 }}
                    />

                    <AutocompleteWithLoading
                        value={relativeTo}
                        id="relative-to-selector"
                        options={timeRelativeToOptions}
                        onChange={(value) => setFormValue("relativeTo", value)}
                        getOptionLabel={(option) => getRelativeToText(option as TimeRelativeTo, translate)}
                        getOptionSelected={(option) => option === relativeTo}
                        loadingOptions={false}
                        disableClearable
                        disabled={!!disableInputs.relativeTo?.(state)}
                        style={{ flexGrow: 1, ...(hiddenInputs?.relativeTo?.(state) ? { display: "none" } : {}) }}
                    />

                    <AutocompleteWithLoading
                        value={relationship}
                        id="relationship-selector"
                        options={Object.values(RelativeEventRuleRelationship)}
                        onChange={(value) => setFormValue("relationship", value)}
                        getOptionLabel={(option) => getRelationshipText(option as RelativeEventRuleRelationship, translate)}
                        getOptionSelected={(option) => option === relationship}
                        loadingOptions={false}
                        disableClearable
                        disabled={!!disableInputs.relationship?.(state)}
                        style={{ flexGrow: 1, ...(hiddenInputs?.relationship?.(state) ? { display: "none" } : {}) }}
                    />

                    <AutocompleteWithLoading
                        value={relatedType}
                        options={relatableEventBlockTypes}
                        onChange={(value) => setFormValue("relatedType", value)}
                        getOptionLabel={(option) => getRelatedTypeText(option as ScheduleConfigEventBlockType, translate)}
                        getOptionSelected={(option) => option.id === relatedType.id}
                        loadingOptions={false}
                        disableClearable
                        disabled={!!disableInputs.relatedType?.(state)}
                        style={{ flexGrow: 1, ...(hiddenInputs?.relatedType?.(state) ? { display: "none" } : {}) }}
                    />
                </div>
            </div>
            {relationship === RelativeEventRuleRelationship.Specific && (
                <div className="field-group">
                    <AutocompleteWithLoading<PotentialEventBlocksToRelateTo>
                        value={specificRelatedToEvent}
                        placeholder={translate(Content.common.buttons.select)}
                        options={eventBlocksToRelateToOptions}
                        onChange={(value) => setFormValue("specificRelatedToEvent", value)}
                        getOptionLabel={(option: PotentialEventBlocksToRelateTo) =>
                            option.startAt != null && option.endAt != null
                                ? `${option?.name} (${format(formatDateInTimezone(option?.startAt, timezone), "HH:mm")} - ${format(
                                      formatDateInTimezone(option?.endAt, timezone),
                                      "HH:mm"
                                  )})`
                                : option?.name
                        }
                        getOptionSelected={(option) => option.id === specificRelatedToEvent?.id}
                        loadingOptions={false}
                        disableClearable
                        disabled={!!disableInputs.specificRelatedToEvent?.(state)}
                        style={{ flexGrow: 1, ...(hiddenInputs?.specificRelatedToEvent?.(state) ? { display: "none" } : {}) }}
                    />
                </div>
            )}

            {!(hiddenInputs.repeatActivity?.(state) && hiddenInputs.doesRepeatActivity?.(state)) && (
                <div className="field-group">
                    {!hiddenInputs.doesRepeatActivity?.(state) && (
                        <>
                            <StyledFieldLabel>{translate(Content.timerules.repeat_activity.repeat_activity)}</StyledFieldLabel>
                            <div className="checkbox-form-control">
                                <CheckBoxStyled
                                    disabled={!!disableInputs.doesRepeatActivity?.(state)}
                                    styleAdditions={{ padding: "0px" }}
                                    checked={doesRepeatActivity}
                                    onChange={(e) => setFormValue("doesRepeatActivity", e.currentTarget.checked)}
                                />
                                <Label1>{translate(Content.timerules.repeat_activity.repeat_activity)}</Label1>
                            </div>
                        </>
                    )}
                    {!hiddenInputs?.repeatActivity?.(state) && doesRepeatActivity && (
                        <ConciseSelector<string, true>
                            options={Object.keys(scheduleDaysMap).map((day) => ({
                                id: day.toString(),
                                label: `${translate(Content.schedule.day)} ${day} (${format(scheduleDaysMap[day], "dd/MM/yy")})`,
                            }))}
                            onChange={(value) => setFormValue("repeatActivity", value)}
                            values={repeatActivity}
                            multi
                            renderLabel={(selection) =>
                                selection.length === 0
                                    ? translate(Content.timerules.repeat_activity.none_selected)
                                    : selection.length === Array.from(scheduleDaysMap.keys()).length
                                    ? translate(Content.timerules.repeat_activity.repeat_on_all)
                                    : selection.map((day: string) => format(scheduleDaysMap[Number(day)], "dd/MM")).join(", ")
                            }
                            disabled={!!disableInputs.repeatActivity?.(state)}
                            toggleAllOptions
                            toggleAllOptionsLabel={translate(Content.timerules.repeat_activity.all_days)}
                            className="repeat-activity-selector"
                        />
                    )}
                </div>
            )}
            {!hiddenInputs.duration?.(state) && (
                <div className="time-selector-group field-group">
                    <TimeSelectorWrapper
                        timeName="duration"
                        className="duration field-group"
                        value={duration}
                        onChange={(e) => setFormValue("duration", e.target.value as TimeSpan)}
                        error={!!timeErrorCheck(duration)}
                        helperText={timeErrorCheck(duration)}
                        disabled={!!disableInputs.duration?.(state)}
                    />
                </div>
            )}
        </StyledFormContainer>
    );
};

const StyledFormContainer = styled.div`
    .line-items-large-display {
        display: flex;
        gap: 5px;
        flex-wrap: wrap;

        .time-selector-container {
            width: min-content;
            margin-bottom: 5px;
        }

        ${customMediaQueryMax(screenSizeBreakpoints.medium)} {
            .MuiAutocomplete-root,
            .time-selector {
                flex-basis: calc(50% - 2.5px /* Account for gap property */);
                flex-shrink: 1;
            }
        }

        ${customMediaQueryMin(screenSizeBreakpoints.medium)} {
            #after-before-selector {
                max-width: 105px;
            }

            #relationship-selector {
                max-width: 98px;
            }

            #relative-to-selector {
                min-width: 60.5px;
            }
        }
    }

    .field-group:not(.field-group:first-of-type) {
        margin-top: 15px;

        .checkbox-form-control {
            ${checkboxWithLabelCss}
            margin-bottom: 15px;
        }

        .repeat-activity-selector {
            .MuiSelect-outlined {
                border: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.three};
            }
        }
    }

    ${muiInputFormsCSS}

    ${media.extraSmall} {
        padding: 0;
    }
`;

export function getRelativeToText(relativeTo: TimeRelativeTo, translate: UseIventisTranslate) {
    switch (relativeTo) {
        case TimeRelativeTo.StartTime:
            return translate(Content.timerules.relative_to.start_time);
        case TimeRelativeTo.EndTime:
            return translate(Content.timerules.relative_to.end_time);
        case TimeRelativeTo.ZeroTime:
            return translate(Content.timerules.relative_to.zero_time);
        default:
            throw new Error(`Relative to "${relativeTo}" not handled`);
    }
}

function getRelationshipText(relationship: RelativeEventRuleRelationship, translate: UseIventisTranslate) {
    switch (relationship) {
        case RelativeEventRuleRelationship.Every:
            return translate(Content.timerules.relationship.every);
        case RelativeEventRuleRelationship.FirstOfDay:
            return translate(Content.timerules.relationship.first);
        case RelativeEventRuleRelationship.LastOfDay:
            return translate(Content.timerules.relationship.last);
        case RelativeEventRuleRelationship.Specific:
            return translate(Content.timerules.relationship.specific);
        default:
            throw new Error(`Relationship "${relationship}" not handled`);
    }
}

function getRelatedTypeText(relatedType: ScheduleConfigEventBlockType, translate: UseIventisTranslate) {
    switch (relatedType.calendarLevel) {
        case 1:
            // Session
            return translate(Content.timerules.related_type.Session);
        case 2:
            // Schedule item
            return translate(Content.timerules.related_type["Scheduled item"]);
        default:
            throw new Error(`Calendar level not supported ${relatedType.calendarLevel}`);
    }
}
